<template>
  <StudioDropdown
    useFixedPosition
    :isSlotEnabled="true"
    :selectedItemIndex="selectedItemIndex"
    :menuItems="menuItems"
    :position="openDirection"
    :extraDropdownClasses="'tw-w-80'"
    @select="selectMenuItem"
  >
    <template #triggerButton="{ toggleDropdownMenu, setRef }">
      <div class="tw-rounded-lg tw-bg-white" @click.stop>
        <StudioButton
          :ref="el => setRef(el)"
          trackingId="video-library-table-item-quick-actions"
          variant="outline"
          @click="toggleDropdownMenu"
        >
          <template #icon> <TablerDotsVertical /> </template>
        </StudioButton>
      </div>
    </template>
  </StudioDropdown>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { canPreviewVideoAsset, canRenameVideoAsset, getIsVideoAssetEditable, VideoAsset } from '../videoLibraryService';

import TablerDotsVertical from '@/studioComponents/Icons/tabler/TablerDotsVertical.vue';
import StudioButton from '@/studioComponents/StudioButton.vue';
import StudioDropdown, { MenuItem } from '@/studioComponents/StudioDropdown.vue';
import { isEURegion } from '@/utils';

const props = withDefaults(
  defineProps<{
    videoAsset: VideoAsset;
    selected: boolean;
    openDirection: 'top-right' | 'bottom-right';
    isProcessingDisabled?: boolean;
  }>(),
  {
    isProcessingDisabled: false
  }
);

const emit = defineEmits<{
  (e: 'trigger:edit', videoAsset: VideoAsset): void;
  (e: 'trigger:rename', videoAsset: VideoAsset): void;
  (e: 'trigger:download', videoAsset: VideoAsset): void;
  (e: 'trigger:download-seo-geo', videoAsset: VideoAsset): void;
  (e: 'trigger:delete', videoAsset: VideoAsset): void;
  (e: 'trigger:preview', videoAsset: VideoAsset): void;
}>();

const menuItems = ref([
  {
    id: 0,
    title: 'Edit',
    hidden: isEURegion() || props.videoAsset.source === 'UPLOAD' || props.videoAsset.status === 'ERROR',
    disabled: !getIsVideoAssetEditable(props.videoAsset) || props.isProcessingDisabled,
    disabledReason: props.isProcessingDisabled
      ? 'Editing is unavailable for this event because Content Lab processing is disabled'
      : 'This video is not ready yet. Please try again after some time.'
  },
  {
    id: 1,
    title: 'Rename',
    disabled: !canRenameVideoAsset(props.videoAsset),
    disabledReason:
      props.videoAsset.status === 'ERROR' ? '' : 'This video is not ready yet. Please try again after some time.'
  },
  {
    id: 2,
    title: 'Download video',
    disabled: !props.videoAsset?.urls?.mp4,
    disabledReason:
      props.videoAsset.status === 'ERROR' ? '' : 'This video is not ready yet. Please try again after some time.'
  },
  {
    id: 3,
    title: 'Download SEO/GEO Assets',
    subTitle: 'Transcript and metadata to allow your video to be more discoverable by search engines and LLMs.',
    titleExtraClasses: 'tw-whitespace-nowrap',
    disabled: !props.videoAsset?.urls?.mp4,
    disabledReason:
      props.videoAsset.status === 'ERROR' ? '' : 'This video is not ready yet. Please try again after some time.'
  },
  {
    id: 4,
    title: 'Delete',
    hidden: ['EVENT_RECORDING', 'DIRECT_RECORDING'].includes(props.videoAsset.source ?? ''),
    disabled: props.selected || props.videoAsset.status === 'PROGRESSING',
    disabledReason:
      props.videoAsset.status === 'PROGRESSING'
        ? 'This video is not ready yet. Please try again after some time.'
        : 'Please deselect this video before deleting it from the library.'
  },
  {
    id: 5,
    title: 'Preview',
    disabled: !canPreviewVideoAsset(props.videoAsset),
    disabledReason:
      props.videoAsset.status === 'ERROR' ? '' : 'This video is not ready yet. Please try again after some time.'
  }
] as MenuItem[]);

const selectedItemIndex = ref(0);

const selectMenuItem = (id: number) => {
  if (id === 0) {
    emit('trigger:edit', props.videoAsset);
  } else if (id === 1) {
    emit('trigger:rename', props.videoAsset);
  } else if (id === 2) {
    emit('trigger:download', props.videoAsset);
  } else if (id === 3) {
    emit('trigger:download-seo-geo', props.videoAsset);
  } else if (id === 4) {
    emit('trigger:delete', props.videoAsset);
  } else if (id === 5) {
    emit('trigger:preview', props.videoAsset);
  }
};
</script>
